import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../../config/apiConfig';
import AdminHubPageFrame from '../../components/admin/AdminHubPageFrame';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';

const PROVIDERS = [
  { value: 'email', label: 'Email OTP' },
  { value: 'sms_twilio', label: 'Twilio SMS OTP' },
  { value: 'auto', label: 'Auto (Twilio → Email fallback)' },
];

function yesNo(v) {
  return v ? 'Yes' : 'No';
}

const ADMIN_FETCH_INIT = {
  credentials: 'include',
  mode: 'cors',
};

export default function AdminAuthLoginPage() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState('');
  const [status, setStatus] = useState(null);
  const [otpProvider, setOtpProvider] = useState('email');
  const [passwordLoginEnabled, setPasswordLoginEnabled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN_AUTH_LOGIN_SETTINGS), {
        ...ADMIN_FETCH_INIT,
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error('load failed');
      setStatus(data);
      setOtpProvider(data.settings?.otp_provider || 'email');
      setPasswordLoginEnabled(Boolean(data.settings?.password_login_enabled));
    } catch {
      setErr('تعذر تحميل إعدادات تسجيل الدخول.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setErr('');
    setSaved('');
    try {
      const res = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN_AUTH_LOGIN_SETTINGS), {
        ...ADMIN_FETCH_INIT,
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          settings: {
            otp_provider: otpProvider,
            password_login_enabled: passwordLoginEnabled,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error('save failed');
      setStatus(data);
      setSaved('تم حفظ الإعدادات.');
    } catch {
      setErr('تعذر حفظ الإعدادات.');
    } finally {
      setSaving(false);
    }
  }

  const crumbs = [
    { label: t('adminHub.navPlatform'), to: '/admin/platform/auth-login' },
    { label: t('adminHub.authLoginTitle') },
  ];

  return (
    <AdminHubPageFrame
      titleKey="adminHub.authLoginTitle"
      descKey="adminHub.authLoginDesc"
      breadcrumbItems={crumbs}
    >
      <section className="admin-section-card">
        <AdminSectionHeader title="Login settings" subtitle="system_settings → auth.login" />

        {loading ? <p className="admin-muted">جاري التحميل...</p> : null}
        {err ? (
          <div className="admin-state admin-state--error" role="alert">
            {err}
          </div>
        ) : null}
        {saved ? (
          <div className="admin-state admin-state--ok" role="status">
            {saved}
          </div>
        ) : null}

        {!loading && status ? (
          <>
            <div className="admin-kv-grid" style={{ marginBottom: '1.5rem' }}>
              <div>
                <strong>Current Provider:</strong> {status.currentProvider}
              </div>
              <div>
                <strong>Email Configured:</strong> {yesNo(status.emailConfigured)}
              </div>
              <div>
                <strong>Twilio Configured:</strong> {yesNo(status.twilioConfigured)}
              </div>
              <div>
                <strong>Password Login Enabled:</strong> {yesNo(status.passwordLoginEnabled)}
              </div>
              <div>
                <strong>OTP Login Viable:</strong> {yesNo(status.otpLoginEnabled)}
              </div>
            </div>

            <form onSubmit={handleSave} className="admin-form-stack">
              <div className="admin-form-field">
                <label htmlFor="otpProvider">OTP Provider</label>
                <select
                  id="otpProvider"
                  value={otpProvider}
                  onChange={(e) => setOtpProvider(e.target.value)}
                  disabled={saving}
                >
                  {PROVIDERS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="admin-form-field">
                <label htmlFor="passwordLogin">Password Login</label>
                <select
                  id="passwordLogin"
                  value={passwordLoginEnabled ? 'true' : 'false'}
                  onChange={(e) => setPasswordLoginEnabled(e.target.value === 'true')}
                  disabled={saving}
                >
                  <option value="false">Disable (hidden from UI)</option>
                  <option value="true">Enable (show password tab)</option>
                </select>
              </div>

              <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                {saving ? 'جاري الحفظ...' : 'حفظ'}
              </button>
            </form>
          </>
        ) : null}
      </section>
    </AdminHubPageFrame>
  );
}
